import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccounts } from "../../logic/hooks/useAccounts.js";
import { useCloseAccountDetail, useDetailAccountId, useOpenAccountDetail } from "../../logic/stores/accountsStore.js";
import { AccountsRouteSkeleton } from "../../components/skeletons/AccountsRouteSkeleton.jsx";
import { AccountDetailSkeleton } from "../../components/skeletons/AccountDetailSkeleton.jsx";
import { AccountsListPanel } from "./AccountsListPanel.jsx";
import { AccountDetailPage } from "./AccountDetailPage.jsx";
import { CreateAccountModal } from "./components/CreateAccountModal.jsx";

export function AccountsPage() {
  const { items, allItems, loading, search, setSearch, saveAccount, removeAccount, saving } = useAccounts();
  const detailAccountId = useDetailAccountId();
  const openAccountDetail = useOpenAccountDetail();
  const closeAccountDetail = useCloseAccountDetail();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const detailAccount = useMemo(
    () => allItems.find((a) => a.id === detailAccountId) ?? null,
    [allItems, detailAccountId],
  );

  useEffect(() => {
    if (detailAccountId && !detailAccount && !loading) {
      closeAccountDetail();
    }
  }, [detailAccountId, detailAccount, loading, closeAccountDetail]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setFormKey((k) => k + 1);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((account) => {
    setEditing(account);
    setFormKey((k) => k + 1);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const handleSave = useCallback(async (payload) => {
    await saveAccount(payload);
  }, [saveAccount]);

  const handleDelete = useCallback(async (id) => {
    if (detailAccountId === id) closeAccountDetail();
    await removeAccount(id);
  }, [detailAccountId, closeAccountDetail, removeAccount]);

  const handleOpen = useCallback((account) => {
    openAccountDetail(account.id);
  }, [openAccountDetail]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {detailAccountId ? <AccountDetailSkeleton /> : <AccountsRouteSkeleton />}
      </div>
    );
  }

  if (detailAccount) {
    return (
      <>
        <AccountDetailPage account={detailAccount} accounts={allItems} onEdit={openEdit} onDelete={handleDelete} />
        <CreateAccountModal
          open={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          editing={editing}
          formKey={formKey}
          saving={saving}
        />
      </>
    );
  }

  return (
    <>
      <AccountsListPanel
        accounts={items}
        allAccounts={allItems}
        search={search}
        onSearchChange={setSearch}
        onAdd={openCreate}
        onOpen={handleOpen}
        onEdit={openEdit}
        onDelete={handleDelete}
        loading={loading}
      />
      <CreateAccountModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        editing={editing}
        formKey={formKey}
        saving={saving}
      />
    </>
  );
}
